import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import Select from "react-select";
import { Button } from "../components/Button";
import { Loader } from "../components/Loader";
import { formatCurrency, formatInteger } from "../helpers/formatNumber";
import { FailedTransactionsAdminService } from "../api/generated/services/FailedTransactionsAdminService";
import { FailedTransactionViewModel } from "../api/generated/models/FailedTransactionViewModel";
import { FailedTransactionSource } from "../api/generated/models/FailedTransactionSource";
import {
  buildAdminFailedTransactionsQuery,
  emptyFailedFilterDraft,
  failedFiltersToSearchParams,
  searchParamsToFailedFilters,
  AdminFailedTransactionsFilterDraft,
  StatusFilter,
} from "../helpers/adminFailedTransactionsQuery";
import "../api/openApiConfig";
import FailedTransactionDetailsModal from "../components/AdminFailedTransactions/FailedTransactionDetailsModal";

const PAGE_SIZE = 100;

const SOURCE_OPTIONS = Object.values(FailedTransactionSource).map((value) => ({
  value,
  label: value,
}));

const STATUS_OPTIONS: StatusFilter[] = ["Failed", "Resolved", "All"];

const selectStyles = {
  control: (base: any) => ({
    ...base,
    borderColor: "#d1d5db",
    minHeight: "2.5rem",
    boxShadow: "none",
    "&:hover": { borderColor: "#9ca3af" },
  }),
};

const AdminFailedTransactions: React.FC = () => {
  const [transactions, setTransactions] = useState<FailedTransactionViewModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // URL is the single source of truth for applied filters and the 1-based page.
  const { filters: appliedFilters, page: pageNumber } = useMemo(
    () => searchParamsToFailedFilters(searchParams),
    [searchParams]
  );
  const [total, setTotal] = useState(0);
  const [errorCodeOptions, setErrorCodeOptions] = useState<string[]>([]);
  // Draft = what the user is editing; applied (from the URL) drives fetches.
  const [filters, setFilters] = useState<AdminFailedTransactionsFilterDraft>(
    () => searchParamsToFailedFilters(searchParams).filters
  );
  const [selectedTransaction, setSelectedTransaction] =
    useState<FailedTransactionViewModel | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [stats, setStats] = useState<{ totalCount: number; totalAmount: number }>({
    totalCount: 0,
    totalAmount: 0,
  });

  useEffect(() => {
    setFilters(appliedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    let ignore = false;
    const run = async () => {
      try {
        setLoading(true);
        const query = buildAdminFailedTransactionsQuery({
          filters: appliedFilters,
          pageNumber: pageNumber - 1, // backend pagination is 0-based
          pageSize: PAGE_SIZE,
        });
        const response = await FailedTransactionsAdminService.postApiFailedTransactionsAdminList(query);
        if (ignore) return;
        setTransactions(response.data?.data ?? []);
        setTotal(response.data?.total ?? 0);
        setStats({
          totalCount: response.stats?.totalCount ?? 0,
          totalAmount: response.stats?.totalAmount ?? 0,
        });
        setErrorCodeOptions(response.availableErrorCodes ?? []);
        setError(null);
      } catch (err) {
        if (ignore) return;
        setError("Failed to fetch failed transactions");
        console.error("Error fetching failed transactions:", err);
      } finally {
        if (!ignore) {
          setLoading(false);
          setInitialLoad(false);
        }
      }
    };
    run();
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  if (initialLoad) {
    return (
      <div className="loading-page">
        <Loader />
      </div>
    );
  }

  const handleApplyFilters = () => {
    setSearchParams(failedFiltersToSearchParams(filters, 1));
  };

  const handleResetFilters = () => {
    setFilters(emptyFailedFilterDraft);
    setSearchParams(new URLSearchParams());
  };

  const openDetails = (t: FailedTransactionViewModel) => {
    setSelectedTransaction(t);
    setDetailsModalOpen(true);
  };

  const formatCopenhagen = (iso?: string | null) =>
    iso
      ? new Date(iso).toLocaleString("da-DK", {
          dateStyle: "short",
          timeStyle: "short",
          timeZone: "Europe/Copenhagen",
        })
      : "-";

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <div className="flex flex-col gap-4 mb-10">
        <h2 className="font-medium">Failed Transactions</h2>
        <div className="flex items-center gap-4 flex-wrap mb-4">
          <div className="w-[250px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date (Copenhagen)
            </label>
            <input
              type="date"
              className="w-full p-2 border border-gray-300 rounded-md"
              value={filters.startDate}
              onChange={(e) => setFilters((f) => ({ ...f, startDate: e.target.value }))}
            />
          </div>
          <div className="w-[250px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date (Copenhagen)
            </label>
            <input
              type="date"
              className="w-full p-2 border border-gray-300 rounded-md"
              value={filters.endDate}
              onChange={(e) => setFilters((f) => ({ ...f, endDate: e.target.value }))}
            />
          </div>
          <div className="w-[250px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Sources</label>
            <Select
              isMulti
              options={SOURCE_OPTIONS}
              value={filters.sources.map((s) => ({ value: s, label: s }))}
              onChange={(selected) =>
                setFilters((f) => ({ ...f, sources: selected.map((o) => o.value) }))
              }
              placeholder="All sources"
              styles={selectStyles}
            />
          </div>
          <div className="w-[300px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Error Codes</label>
            <Select
              isMulti
              options={errorCodeOptions.map((c) => ({ value: c, label: c }))}
              value={filters.errorCodes.map((c) => ({ value: c, label: c }))}
              onChange={(selected) =>
                setFilters((f) => ({ ...f, errorCodes: selected.map((o) => o.value) }))
              }
              placeholder="All error codes"
              styles={selectStyles}
            />
          </div>
          <div className="w-[160px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              className="w-full p-2 border border-gray-300 rounded-md h-[42px]"
              value={filters.status}
              onChange={(e) =>
                setFilters((f) => ({ ...f, status: e.target.value as StatusFilter }))
              }
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="w-[140px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Min Amount (kr)</label>
            <input
              type="number"
              className="w-full p-2 border border-gray-300 rounded-md"
              value={filters.minAmountKr}
              onChange={(e) => setFilters((f) => ({ ...f, minAmountKr: e.target.value }))}
            />
          </div>
          <div className="w-[140px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Max Amount (kr)</label>
            <input
              type="number"
              className="w-full p-2 border border-gray-300 rounded-md"
              value={filters.maxAmountKr}
              onChange={(e) => setFilters((f) => ({ ...f, maxAmountKr: e.target.value }))}
            />
          </div>
          <div className="w-[260px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Identity (email or GUID)
            </label>
            <input
              type="text"
              className="w-full p-2 border border-gray-300 rounded-md"
              value={filters.identity}
              onChange={(e) => setFilters((f) => ({ ...f, identity: e.target.value }))}
            />
          </div>
          <div className="w-[260px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Merchant (name or CVR)
            </label>
            <input
              type="text"
              className="w-full p-2 border border-gray-300 rounded-md"
              value={filters.merchantSearch}
              onChange={(e) => setFilters((f) => ({ ...f, merchantSearch: e.target.value }))}
            />
          </div>
        </div>
        <div className="flex items-center gap-3 mb-4">
          <Button onClick={handleApplyFilters}>Apply filters</Button>
          <Button variant="outline" onClick={handleResetFilters}>
            Reset
          </Button>
        </div>
        <div className="flex gap-4">
          <div className="bg-white rounded-lg shadow p-4 min-w-[200px] w-fit">
            <div className="text-gray-500 text-sm">Failed Transactions</div>
            <div className="text-2xl font-semibold mt-1">{formatInteger(stats.totalCount)}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 min-w-[200px] w-fit">
            <div className="text-gray-500 text-sm">Total Amount</div>
            <div className="text-2xl font-semibold mt-1">{formatCurrency(stats.totalAmount)}</div>
          </div>
        </div>
      </div>

      {loading && !initialLoad && (
        <div className="flex justify-center my-8">
          <Loader />
        </div>
      )}

      {error && <div className="error mb-4">{error}</div>}

      <div className="overflow-x-auto">
        <table className="table-default">
          <thead>
            <tr>
              <th className="whitespace-nowrap">Failed At</th>
              <th>Source</th>
              <th>Status</th>
              <th className="whitespace-nowrap">Error Code</th>
              <th>Error Message</th>
              <th>Merchant</th>
              <th className="max-w-[120px]">Amount</th>
              <th className="whitespace-nowrap">Identity ID</th>
              <th>
                <span className="sr-only">Details</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((transaction) => (
              <tr key={transaction.id}>
                <td className="whitespace-nowrap">{formatCopenhagen(transaction.failedAtUtc)}</td>
                <td>{transaction.source}</td>
                <td>
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      transaction.status === "Failed"
                        ? "bg-red-100 text-red-800"
                        : "bg-green-100 text-green-800"
                    }`}
                  >
                    {transaction.status}
                  </span>
                </td>
                <td className="whitespace-nowrap">{transaction.errorCode || "-"}</td>
                <td className="max-w-[280px]">
                  <span className="block truncate" title={transaction.errorMessage ?? undefined}>
                    {transaction.errorMessage || "-"}
                  </span>
                </td>
                <td>{transaction.merchantName || transaction.merchantCvr || transaction.contentfulBusinessId || "-"}</td>
                <td className="max-w-[120px]">
                  {transaction.amount !== null && transaction.amount !== undefined
                    ? formatCurrency(transaction.amount)
                    : "-"}
                </td>
                <td className="font-mono text-xs whitespace-nowrap">
                  {transaction.identityId || "-"}
                </td>
                <td className="whitespace-nowrap">
                  <Button variant="outline" size="sm" onClick={() => openDetails(transaction)}>
                    Details
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between items-center mt-4">
        <Button
          onClick={() =>
            setSearchParams(
              failedFiltersToSearchParams(appliedFilters, Math.max(1, pageNumber - 1))
            )
          }
          disabled={pageNumber === 1}
          variant="outline"
        >
          Previous
        </Button>
        <span>
          Page {pageNumber} of {totalPages}
        </span>
        <Button
          onClick={() =>
            setSearchParams(failedFiltersToSearchParams(appliedFilters, pageNumber + 1))
          }
          disabled={pageNumber >= totalPages}
          variant="outline"
        >
          Next
        </Button>
      </div>
      <FailedTransactionDetailsModal
        isOpen={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        transaction={selectedTransaction}
      />
    </div>
  );
};

export default AdminFailedTransactions;
